import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = "https://dailydish.in/api";
const tok = () => localStorage.getItem("authToken");
const hdr = () => ({ Authorization: `Bearer ${tok()}` });

/* ─────────────────────────── THEME ─────────────────────────────────────── */
const T = {
  bg:       "#F7F8FA",
  surface:  "#FFFFFF",
  border:   "#E5E8EF",
  borderL:  "#EEF0F5",
  text:     "#111827",
  muted:    "#6B7280",
  faint:    "#9CA3AF",
  accent:   "#E8500A",
  blue:     "#2563EB",
  green:    "#16A34A",
  yellow:   "#CA8A04",
  red:      "#DC2626",
  purple:   "#7C3AED",
  teal:     "#0891B2",
  shadow:   "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
  shadowHover: "0 4px 12px rgba(0,0,0,0.10)",
};

const PALETTE = [T.accent, T.blue, T.green, T.yellow, T.purple, T.teal, "#DB2777"];

/* ─────────────────────────── HELPERS ───────────────────────────────────── */
function fmt(n)  { return Number(n || 0).toLocaleString("en-IN"); }
function fmtR(n) { return "₹" + Number(n || 0).toLocaleString("en-IN"); }
function pct(a, b) { return b ? ((a / b) * 100).toFixed(1) : "0.0"; }

/* ─────────────────────────── BAR CHART ─────────────────────────────────── */
function BarChart({ labels = [], values = [], color = T.accent, height = 130, prefix = "", suffix = "" }) {
  const [tip, setTip] = useState(null);
  const max = Math.max(...values, 1);
  const barW = Math.max(12, Math.floor(320 / Math.max(labels.length, 1)) - 6);

  return (
    <div data-chart style={{ position: "relative" }}>
      {tip && (
        <div style={{
          position: "absolute", zIndex: 20,
          left: tip.x, top: tip.y - 48,
          background: T.text, color: "#fff",
          borderRadius: 6, padding: "5px 10px",
          fontSize: 12, fontWeight: 600,
          pointerEvents: "none", whiteSpace: "nowrap",
          transform: "translateX(-50%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          {tip.label}: {prefix}{fmt(tip.value)}{suffix}
          <div style={{ position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `5px solid ${T.text}` }} />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height, overflowX: "auto", paddingBottom: 2 }}>
        {values.map((v, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flexShrink: 0, minWidth: barW }}>
            <div
              style={{
                width: "100%",
                height: Math.max((v / max) * (height - 22), v > 0 ? 4 : 0),
                background: `linear-gradient(180deg, ${color}, ${color}88)`,
                borderRadius: "4px 4px 2px 2px",
                cursor: "pointer",
                transition: "opacity 0.15s",
              }}
              onMouseEnter={e => {
                const r = e.currentTarget.getBoundingClientRect();
                const pr = e.currentTarget.closest("[data-chart]").getBoundingClientRect();
                setTip({ label: labels[i], value: v, x: r.left - pr.left + r.width / 2, y: r.top - pr.top });
              }}
              onMouseLeave={() => setTip(null)}
            />
            <span style={{ fontSize: 9, color: T.faint, whiteSpace: "nowrap", overflow: "hidden", maxWidth: barW, textAlign: "center", textOverflow: "ellipsis" }}>
              {labels[i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── LINE CHART ────────────────────────────────── */
function LineChart({ labels = [], datasets = [], height = 130 }) {
  const [tip, setTip] = useState(null);
  const W = 500, H = height;
  const allVals = datasets.flatMap(d => d.values);
  const max = Math.max(...allVals, 1);
  const min = 0;
  const range = max - min || 1;
  const px = (i) => (i / Math.max(labels.length - 1, 1)) * W;
  const py = (v) => H - 18 - ((v - min) / range) * (H - 28);

  return (
    <div data-chart style={{ position: "relative" }}>
      {tip && (
        <div style={{
          position: "absolute", zIndex: 20,
          left: tip.x, top: tip.y - 52,
          background: T.text, color: "#fff",
          borderRadius: 6, padding: "6px 12px",
          fontSize: 12, pointerEvents: "none",
          transform: "translateX(-50%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          whiteSpace: "nowrap",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{tip.label}</div>
          {tip.items.map((it, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: it.color, display: "inline-block" }} />
              <span style={{ color: "#ccc", fontSize: 11 }}>{it.label}:</span>
              <span style={{ fontWeight: 600 }}>{fmt(it.value)}</span>
            </div>
          ))}
          <div style={{ position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `5px solid ${T.text}` }} />
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height }} preserveAspectRatio="none">
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
          <line key={i} x1={0} y1={py(min + f * range)} x2={W} y2={py(min + f * range)}
            stroke={T.borderL} strokeWidth="1" />
        ))}
        {datasets.map((ds, di) => {
          const pts = ds.values.map((v, i) => `${px(i)},${py(v)}`).join(" ");
          const fillPts = `0,${py(0)} ${pts} ${px(ds.values.length - 1)},${py(0)}`;
          return (
            <g key={di}>
              <defs>
                <linearGradient id={`lg${di}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ds.color} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={ds.color} stopOpacity="0" />
                </linearGradient>
              </defs>
              <polygon points={fillPts} fill={`url(#lg${di})`} />
              <polyline points={pts} fill="none" stroke={ds.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {ds.values.map((v, i) => (
                <circle key={i} cx={px(i)} cy={py(v)} r="4" fill={T.surface} stroke={ds.color} strokeWidth="2"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={e => {
                    const svg = e.currentTarget.closest("svg");
                    const svgRect = svg.getBoundingClientRect();
                    const ptRect = e.currentTarget.getBoundingClientRect();
                    setTip({
                      label: labels[i],
                      x: ptRect.left - svgRect.left + 4,
                      y: ptRect.top - svgRect.top,
                      items: datasets.map(d => ({ label: d.label, value: d.values[i], color: d.color })),
                    });
                  }}
                  onMouseLeave={() => setTip(null)}
                />
              ))}
            </g>
          );
        })}
        {labels.map((l, i) => (
          <text key={i} x={px(i)} y={H - 2} textAnchor="middle" fontSize="9" fill={T.faint}>{l}</text>
        ))}
      </svg>
    </div>
  );
}

/* ─────────────────────────── DONUT ─────────────────────────────────────── */
function Donut({ segments = [], size = 110 }) {
  const [tip, setTip] = useState(null);
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = 40, cx = 55, cy = 55, circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {tip && (
        <div style={{
          position: "absolute", zIndex: 20, left: "50%", top: -40, transform: "translateX(-50%)",
          background: T.text, color: "#fff", borderRadius: 6, padding: "5px 10px",
          fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", pointerEvents: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          {tip.label}: {tip.value} ({pct(tip.value, total)}%)
        </div>
      )}
      <svg width={size} height={size} viewBox="0 0 110 110">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.border} strokeWidth="14" />
        {segments.map((seg, i) => {
          const p = seg.value / total;
          const arc = { ...seg, p, o: offset };
          offset += p * circ;
          return (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={seg.color} strokeWidth="14"
              strokeDasharray={`${p * circ} ${circ}`}
              strokeDashoffset={-arc.o}
              transform="rotate(-90 55 55)"
              style={{ cursor: "pointer", transition: "opacity 0.15s" }}
              onMouseEnter={() => setTip({ label: seg.label, value: seg.value })}
              onMouseLeave={() => setTip(null)}
            />
          );
        })}
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="14" fontWeight="800" fill={T.text}>{fmt(total)}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill={T.muted}>total</text>
      </svg>
    </div>
  );
}

/* ─────────────────────────── UI ATOMS ──────────────────────────────────── */
const inputSt = {
  background: T.surface, border: `1px solid ${T.border}`,
  borderRadius: 8, color: T.text,
  padding: "8px 12px", fontSize: 13, outline: "none",
  fontFamily: "inherit",
};

function Sel({ label, value, onChange, children, style = {} }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {label && <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ ...inputSt, ...style, cursor: "pointer" }}>
        {children}
      </select>
    </label>
  );
}

function DateIn({ label, value, onChange }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
      <input type="date" value={value} onChange={e => onChange(e.target.value)}
        style={{ ...inputSt, colorScheme: "light" }} />
    </label>
  );
}

function Btn({ children, onClick, disabled, primary }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: primary ? T.accent : T.surface,
      border: `1px solid ${primary ? T.accent : T.border}`,
      color: primary ? "#fff" : T.text,
      borderRadius: 8, padding: "8px 18px",
      fontSize: 13, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      fontFamily: "inherit",
      transition: "box-shadow 0.15s",
      alignSelf: "flex-end",
    }}>{children}</button>
  );
}

function Pill({ label, onRemove, color = T.blue }) {
  return (
    <span style={{
      background: color + "14", border: `1px solid ${color}33`,
      color: color, borderRadius: 20,
      padding: "3px 10px", fontSize: 12, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 6,
    }}>
      {label}
      <span onClick={onRemove} style={{ cursor: "pointer", opacity: 0.6, fontSize: 11 }}>✕</span>
    </span>
  );
}

function Tag({ children, color = T.blue }) {
  return (
    <span style={{
      background: color + "14", color,
      border: `1px solid ${color}33`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
    }}>{children}</span>
  );
}

function StatusTag({ s }) {
  const map = {
    Delivered: T.green, Cancelled: T.red, Confirmed: T.blue,
    Pending: T.yellow, Cooking: T.accent, Packing: T.purple,
    ontheway: T.teal, "On the way": T.teal, Undelivered: T.red,
  };
  return <Tag color={map[s] || T.muted}>{s || "—"}</Tag>;
}

/* ─────────────────────────── KPI CARD ──────────────────────────────────── */
function KPI({ icon, label, value, sub, color = T.accent, loading }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: "18px 20px",
      boxShadow: T.shadow, display: "flex", flexDirection: "column", gap: 6,
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 6 }}>
        <span>{icon}</span>{label}
      </div>
      {loading
        ? <div style={{ height: 28, width: 80, background: T.borderL, borderRadius: 6 }} />
        : <div style={{ fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1.1, letterSpacing: -0.5 }}>{value}</div>
      }
      {sub && <div style={{ fontSize: 12, color: T.faint }}>{sub}</div>}
    </div>
  );
}

/* ─────────────────────────── SECTION TITLE ─────────────────────────────── */
function Sec({ children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: T.text, textTransform: "uppercase", letterSpacing: 0.8 }}>{children}</div>
      {right && <div style={{ fontSize: 12, color: T.muted }}>{right}</div>}
    </div>
  );
}

/* ─────────────────────────── CARD ──────────────────────────────────────── */
function Card({ children, style = {} }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "20px 22px", boxShadow: T.shadow, ...style }}>
      {children}
    </div>
  );
}

/* ─────────────────────────── PROGRESS BAR ──────────────────────────────── */
function Bar({ pct: p, color }) {
  return (
    <div style={{ background: T.borderL, borderRadius: 4, height: 6, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(p, 100)}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.5s ease" }} />
    </div>
  );
}

/* ─────────────────────────── HUB CUSTOMERS TAB ─────────────────────────── */
function HubCustomersTab({ hubs }) {
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [hubStats, setHubStats]       = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [searchQ, setSearchQ]         = useState("");
  const [sortBy, setSortBy]           = useState("primary");
  const [selectedHub, setSelectedHub] = useState(null);
  const [custSearch, setCustSearch]   = useState("");
  const [addrTypeFilter, setAddrTypeFilter] = useState("all");
  const hasFetched = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const custRes = await axios.get(`${API}/User/registeruser`, {
        params: { page: 1, limit: 10000 },
        headers: hdr(),
      });
      const customers = Array.isArray(custRes.data?.success) ? custRes.data.success : [];
      setAllCustomers(customers);

      // Build hub stats from addresses[].hubId
      const primaryCount  = {};
      const anyCount      = {};
      const primaryList   = {};
      const anyList       = {};
      const addrCountMap  = {};
      const addrTypeMap   = {}; // hubId -> { Home:n, PG:n, ... }

      hubs.forEach(h => {
        primaryCount[h._id]  = 0;
        anyCount[h._id]      = 0;
        primaryList[h._id]   = [];
        anyList[h._id]       = [];
        addrCountMap[h._id]  = 0;
        addrTypeMap[h._id]   = {};
      });

      customers.forEach(cust => {
        const addrs = Array.isArray(cust.addresses)
          ? cust.addresses.filter(a => a.isActive !== false && a.hubId)
          : [];

        const hubsForCust = new Set();

        addrs.forEach(addr => {
          const hid = addr.hubId?.toString();
          if (!hid || primaryCount[hid] === undefined) return;
          addrCountMap[hid]++;
          hubsForCust.add(hid);
          const atype = addr.addressType || "Unknown";
          addrTypeMap[hid][atype] = (addrTypeMap[hid][atype] || 0) + 1;
        });

        // Primary = first active address with a hubId
        const primaryAddr = addrs[0];
        if (primaryAddr) {
          const hid = primaryAddr.hubId?.toString();
          if (hid && primaryCount[hid] !== undefined) {
            primaryCount[hid]++;
            primaryList[hid].push(cust);
          }
        }

        hubsForCust.forEach(hid => {
          anyCount[hid]++;
          anyList[hid].push(cust);
        });
      });

      const stats = hubs.map((h, i) => ({
        _id:         h._id,
        hub:         h.hubName || h.name || `Hub ${i + 1}`,
        primary:     primaryCount[h._id] || 0,
        any:         anyCount[h._id]     || 0,
        primaryList: primaryList[h._id]  || [],
        anyList:     anyList[h._id]      || [],
        addrTypes:   addrTypeMap[h._id]  || {},
        avgAddrs:    anyCount[h._id]
          ? (addrCountMap[h._id] / anyCount[h._id]).toFixed(1)
          : "0.0",
        color: PALETTE[i % PALETTE.length],
      }));

      setHubStats(stats);
    } catch (e) {
      setError("Failed to load customer data. " + (e.message || ""));
    } finally {
      setLoading(false);
    }
  }, [hubs]);

  useEffect(() => {
    if (hubs.length > 0 && !hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [hubs, fetchData]);

  const totalCustomers  = allCustomers.length;
  const totalWithHub    = allCustomers.filter(c =>
    c.addresses?.some(a => a.hubId && a.isActive !== false)
  ).length;

  const sorted = [...hubStats]
    .filter(h => !searchQ || h.hub.toLowerCase().includes(searchQ.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "hub")     return a.hub.localeCompare(b.hub);
      if (sortBy === "any")     return b.any - a.any;
      if (sortBy === "avgAddr") return parseFloat(b.avgAddrs) - parseFloat(a.avgAddrs);
      return b.primary - a.primary;
    });

  const maxPrimary   = Math.max(...sorted.map(h => h.primary), 1);
  const totalPrimary = sorted.reduce((s, h) => s + h.primary, 0);

  const selHub = selectedHub ? hubStats.find(h => h._id === selectedHub) : null;

  const filteredCusts = selHub
    ? (addrTypeFilter === "all" ? selHub.primaryList : selHub.primaryList.filter(c =>
        c.addresses?.some(a =>
          a.hubId?.toString() === selHub._id &&
          a.addressType === addrTypeFilter
        )
      ))
    : [];

  const searchedCusts = filteredCusts.filter(c =>
    !custSearch ||
    (c.Fname || "").toLowerCase().includes(custSearch.toLowerCase()) ||
    String(c.Mobile || "").includes(custSearch)
  );

  return (
    <div>
      {/* ── Top filters ── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>Search Hub</span>
            <input
              type="text"
              placeholder="Filter hubs…"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              style={{ ...inputSt, width: 180 }}
            />
          </label>
          <Sel label="Sort By" value={sortBy} onChange={setSortBy}>
            <option value="primary">Primary customers ↓</option>
            <option value="any">Any address ↓</option>
            <option value="hub">Hub name A–Z</option>
            <option value="avgAddr">Avg addresses ↓</option>
          </Sel>
          <Btn primary onClick={fetchData} disabled={loading}>↺ Refresh</Btn>
        </div>
      </Card>

      {error && (
        <div style={{ background: T.red + "10", border: `1px solid ${T.red}33`, borderRadius: 10, padding: "12px 16px", color: T.red, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
          ⚠ {error}
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: T.muted, fontSize: 13 }}>
          <div style={{ width: 18, height: 18, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          Loading customer data…
        </div>
      )}

      {!loading && hubStats.length > 0 && (
        <>
          {/* ── Summary KPIs ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 16 }}>
            <KPI icon="👥" label="Total Customers"   value={fmt(totalCustomers)} color={T.blue}   />
            <KPI icon="📍" label="Hub-Mapped"        value={fmt(totalWithHub)}   sub={pct(totalWithHub, totalCustomers) + "% of all"} color={T.green} />
            <KPI icon="🏠" label="Total Hubs"        value={fmt(hubs.length)}    color={T.accent} />
            <KPI icon="🏆" label="Top Hub"
              value={sorted[0]?.hub || "—"}
              sub={fmt(sorted[0]?.primary || 0) + " primary customers"}
              color={T.purple}
            />
            <KPI icon="📊" label="Avg / Hub"
              value={fmt(Math.round(totalPrimary / Math.max(hubs.length, 1)))}
              sub="primary customers"
              color={T.teal}
            />
          </div>

          {/* ── Hub Cards ── */}
          <Sec children={`Hub Breakdown — ${sorted.length} hub${sorted.length !== 1 ? "s" : ""}`} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 18 }}>
            {sorted.map((h) => {
              const share = totalPrimary ? ((h.primary / totalPrimary) * 100).toFixed(1) : "0.0";
              const isSelected = selectedHub === h._id;
              return (
                <div
                  key={h._id}
                  onClick={() => setSelectedHub(isSelected ? null : h._id)}
                  style={{
                    background: T.surface,
                    border: `1px solid ${isSelected ? h.color : T.border}`,
                    borderLeft: `4px solid ${h.color}`,
                    borderRadius: 12,
                    padding: "16px 18px",
                    boxShadow: isSelected ? `0 0 0 2px ${h.color}33` : T.shadow,
                    cursor: "pointer",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: T.text, maxWidth: "70%", wordBreak: "break-word" }}>{h.hub}</div>
                    <Tag color={h.color}>{share}%</Tag>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    {[
                      { label: "Primary", value: fmt(h.primary), color: h.color },
                      { label: "Any addr.", value: fmt(h.any),     color: T.blue },
                      { label: "Avg addrs", value: h.avgAddrs,    color: T.muted },
                      { label: "Addr types", value: Object.keys(h.addrTypes).length, color: T.muted },
                    ].map(s => (
                      <div key={s.label} style={{ background: T.bg, borderRadius: 8, padding: "8px 10px" }}>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: s.color, marginTop: 2 }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  <Bar pct={parseFloat((h.primary / maxPrimary) * 100)} color={h.color} />
                  <div style={{ fontSize: 11, color: T.faint, marginTop: 5 }}>
                    {share}% of assigned customers • click to view
                  </div>
                </div>
              );
            })}
            {sorted.length === 0 && (
              <div style={{ gridColumn: "1/-1", textAlign: "center", color: T.faint, padding: 40 }}>No hubs match your search</div>
            )}
          </div>

          {/* ── Comparison Table ── */}
          <Card style={{ marginBottom: 16 }}>
            <Sec children="Hub Comparison Table" right={`${sorted.length} hubs`} />
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Hub</th>
                    <th>Primary Customers</th>
                    <th>Any Address</th>
                    <th>Avg Addresses</th>
                    <th>Address Types</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((h, i) => {
                    const share = totalPrimary ? ((h.primary / totalPrimary) * 100).toFixed(1) : "0.0";
                    return (
                      <tr
                        key={h._id}
                        onClick={() => setSelectedHub(selectedHub === h._id ? null : h._id)}
                        style={{ cursor: "pointer", background: selectedHub === h._id ? h.color + "08" : undefined }}
                      >
                        <td style={{ fontWeight: 800, color: T.faint, fontSize: 11 }}>{i + 1}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: h.color, flexShrink: 0 }} />
                            <strong>{h.hub}</strong>
                          </div>
                        </td>
                        <td style={{ fontWeight: 800, fontSize: 15, color: h.color }}>{fmt(h.primary)}</td>
                        <td style={{ color: T.muted }}>{fmt(h.any)}</td>
                        <td style={{ color: T.muted }}>{h.avgAddrs}</td>
                        <td>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {Object.entries(h.addrTypes).map(([type, count]) => (
                              <Tag key={type} color={T.blue}>{type}: {count}</Tag>
                            ))}
                            {Object.keys(h.addrTypes).length === 0 && <span style={{ color: T.faint, fontSize: 12 }}>—</span>}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
                            <div style={{ flex: 1, height: 5, background: T.borderL, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: share + "%", height: "100%", background: h.color, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, color: T.muted, minWidth: 36 }}>{share}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Customer Detail Panel ── */}
          {selHub && (
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: T.text }}>{selHub.hub}</div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                    {fmt(selHub.primary)} primary customers · {fmt(selHub.any)} total (any address)
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {/* Address type filter */}
                  <Sel label="" value={addrTypeFilter} onChange={setAddrTypeFilter} style={{ minWidth: 130 }}>
                    <option value="all">All types</option>
                    {Object.keys(selHub.addrTypes).map(t => (
                      <option key={t} value={t}>{t} ({selHub.addrTypes[t]})</option>
                    ))}
                  </Sel>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="Search customer…"
                      value={custSearch}
                      onChange={e => setCustSearch(e.target.value)}
                      style={{ ...inputSt, width: 170 }}
                    />
                  </label>
                  <Btn onClick={() => { setSelectedHub(null); setCustSearch(""); setAddrTypeFilter("all"); }}>✕ Close</Btn>
                </div>
              </div>

              {/* Address type breakdown donut + bars */}
              {Object.keys(selHub.addrTypes).length > 0 && (
                <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                  <Donut
                    size={90}
                    segments={Object.entries(selHub.addrTypes).map(([k, v], i) => ({
                      label: k, value: v, color: PALETTE[i % PALETTE.length],
                    }))}
                  />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {Object.entries(selHub.addrTypes).map(([k, v], i) => (
                      <div key={k} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                          <span style={{ color: PALETTE[i % PALETTE.length], fontWeight: 700 }}>{k}</span>
                          <span style={{ color: T.muted }}>{v} ({pct(v, selHub.any)}%)</span>
                        </div>
                        <Bar pct={parseFloat(pct(v, selHub.any))} color={PALETTE[i % PALETTE.length]} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Customer list */}
              <Sec children={`Customers with primary address here`} right={`Showing ${Math.min(searchedCusts.length, 100)} of ${filteredCusts.length}`} />
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Address Type</th>
                      <th>Full Address</th>
                      <th>Total Orders</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchedCusts.slice(0, 100).map((c, i) => {
                      const addr = c.addresses?.find(a =>
                        a.hubId?.toString() === selHub._id && a.isActive !== false
                      );
                      const initials = (c.Fname || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
                      const col = PALETTE[i % PALETTE.length];
                      return (
                        <tr key={c._id || i}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{
                                width: 30, height: 30, borderRadius: "50%",
                                background: col + "22", color: col,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, fontWeight: 800, flexShrink: 0,
                              }}>{initials}</div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13 }}>{c.Fname || "—"}</div>
                                <div style={{ fontSize: 11, color: T.faint }}>{c.Email || ""}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontSize: 13, color: T.muted }}>{c.Mobile || "—"}</td>
                          <td>
                            {addr?.addressType
                              ? <Tag color={T.blue}>{addr.addressType}</Tag>
                              : <span style={{ color: T.faint }}>—</span>}
                          </td>
                          <td style={{ fontSize: 12, color: T.muted, maxWidth: 220 }}>
                            <span title={addr?.fullAddress || ""}>
                              {addr?.fullAddress
                                ? addr.fullAddress.length > 55
                                  ? addr.fullAddress.slice(0, 55) + "…"
                                  : addr.fullAddress
                                : "—"}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, color: T.text }}>
                            {fmt(c.totalOrder || c.Nooforders || 0)}
                          </td>
                          <td>
                            <Tag color={c.BlockCustomer === false ? T.red : T.green}>
                              {c.BlockCustomer === false ? "Blocked" : "Active"}
                            </Tag>
                          </td>
                        </tr>
                      );
                    })}
                    {searchedCusts.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: 40, color: T.faint }}>
                          No customers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {searchedCusts.length > 100 && (
                <div style={{ textAlign: "center", padding: "12px 0", fontSize: 12, color: T.faint }}>
                  Showing first 100 of {searchedCusts.length} — use search to narrow down
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {!loading && hubStats.length === 0 && !error && (
        <div style={{ textAlign: "center", color: T.faint, padding: 60 }}>
          Click Refresh to load hub customer data.
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENHANCED SALES REPORT TAB — with ranking, more metrics, and top items
════════════════════════════════════════════════════════════════════════════ */
function SalesReportTab({ hubs }) {
  const [searchTerm, setSearchTerm]         = useState("");
  const [startDate, setStartDate]           = useState(() => new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate]               = useState(() => new Date().toISOString().split("T")[0]);
  const [sortOrder, setSortOrder]           = useState("desc");
  const [filteredData, setFilteredData]     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [selectHub, setSelectHub]           = useState({});
  const [selectedSession, setSelectedSession] = useState("All");
  const [totalSummary, setTotalSummary]     = useState({ qty: 0, revenue: 0, totalOrders: 0, uniqueItems: 0 });
  const [itemRankings, setItemRankings]     = useState([]); // items sorted by quantity sold DESC
  const [categoryStats, setCategoryStats]   = useState({});
  const [avgPricePerItem, setAvgPricePerItem] = useState(0);
  const [topItemRevenue, setTopItemRevenue]   = useState({ name: "", revenue: 0 });
  const [salesTrend, setSalesTrend]         = useState([]); // daily trend data
  const debounceRef = useRef(null);

  const isoStart = (d) => new Date(d + "T00:00:00").toISOString();
  const isoEnd   = (d) => new Date(d + "T23:59:59").toISOString();

  const getSalesReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", isoStart(startDate));
      if (endDate)   params.append("endDate",   isoEnd(endDate));
      if (selectHub?.hubId) params.append("hubId", selectHub.hubId);
      if (selectedSession !== "All") params.append("session", selectedSession);
      if (searchTerm) params.append("searchTerm", searchTerm);
      params.append("sortOrder", sortOrder);

      const r = await axios.get(
        `${API}/admin/getSalesReport?${params.toString()}`,
        { headers: hdr() }
      );
      const data = r.data?.data || [];
      
      // === compute extended metrics ===
      const totalQty = data.reduce((s, i) => s + (i.quantity || 0), 0);
      const totalRev = data.reduce((s, i) => s + (i.totalAmount || 0), 0);
      const totalUnique = data.length;
      
      // Item ranking by quantity sold (highest to lowest)
      const ranked = [...data].sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
      setItemRankings(ranked);
      
      // Top item by revenue
      let topRev = { name: "", revenue: 0 };
      data.forEach(item => {
        if ((item.totalAmount || 0) > topRev.revenue) {
          topRev = { name: item.foodName, revenue: item.totalAmount };
        }
      });
      setTopItemRevenue(topRev);
      
      // Avg price per item (total revenue / total qty)
      setAvgPricePerItem(totalQty ? totalRev / totalQty : 0);
      
      // Category stats (if category field exists, else group by item prefix or default)
      const catMap = {};
      data.forEach(item => {
        const cat = item.category || (item.foodName?.split(" ")[0]) || "Other";
        catMap[cat] = catMap[cat] || { qty: 0, rev: 0 };
        catMap[cat].qty += item.quantity || 0;
        catMap[cat].rev += item.totalAmount || 0;
      });
      setCategoryStats(catMap);
      
      // Daily sales trend (for the date range)
      const trendMap = new Map(); // key: YYYY-MM-DD
      data.forEach(item => {
        if (item.lastSoldTime) {
          const dateKey = new Date(item.lastSoldTime).toISOString().split("T")[0];
          if (!trendMap.has(dateKey)) trendMap.set(dateKey, { orders: 0, qty: 0, rev: 0 });
          const entry = trendMap.get(dateKey);
          entry.orders += 1;
          entry.qty += item.quantity || 0;
          entry.rev += item.totalAmount || 0;
        }
      });
      const trendArray = Array.from(trendMap.entries())
        .map(([date, stats]) => ({ date, ...stats }))
        .sort((a,b) => a.date.localeCompare(b.date));
      setSalesTrend(trendArray);
      
      setFilteredData(data);
      setTotalSummary({
        qty: totalQty,
        revenue: totalRev,
        totalOrders: data.reduce((s, i) => s + (i.orderCount || 1), 0), // approximate
        uniqueItems: totalUnique,
      });
    } catch (e) {
      console.error(e);
      setError("Failed to fetch sales report.");
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectHub, selectedSession, searchTerm, sortOrder]);

  /* auto-fetch with debounce on filter change */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { getSalesReport(); }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [getSalesReport]);

  /* initial fetch */
  useEffect(() => { getSalesReport(); }, []); // eslint-disable-line

  const setDateRange = (type) => {
    const now = new Date();
    const ymd = (d) => d.toISOString().split("T")[0];
    const sub = (d, n, unit) => { const x = new Date(d); if (unit === "d") x.setDate(x.getDate() - n); if (unit === "m") x.setMonth(x.getMonth() - n); return x; };
    const som = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    switch (type) {
      case "today":       setStartDate(ymd(now));              setEndDate(ymd(now)); break;
      case "yesterday":   setStartDate(ymd(sub(now,1,"d")));   setEndDate(ymd(sub(now,1,"d"))); break;
      case "last3Days":   setStartDate(ymd(sub(now,3,"d")));   setEndDate(ymd(now)); break;
      case "lastWeek":    setStartDate(ymd(sub(now,7,"d")));   setEndDate(ymd(now)); break;
      case "thisMonth":   setStartDate(ymd(som(now)));         setEndDate(ymd(now)); break;
      case "last2Months": setStartDate(ymd(som(sub(now,2,"m")))); setEndDate(ymd(now)); break;
      case "last3Months": setStartDate(ymd(som(sub(now,3,"m")))); setEndDate(ymd(now)); break;
      default: break;
    }
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setSearchTerm("");
    setSortOrder("desc");
    setSelectHub({});
    setSelectedSession("All");
  };

  const handleExport = () => {
    const rows = filteredData.map((item, i) => ({
      "Rank":          i+1,
      "Item Name":     item.foodName,
      "Price (₹)":     item.price,
      "Sold Qty":      item.quantity,
      "Total Amount (₹)": item.totalAmount,
      "Last Sold":     item.lastSoldTime ? new Date(item.lastSoldTime).toLocaleString("en-IN") : "—",
    }));
    const headers = Object.keys(rows[0] || {});
    const csv = [
      headers.join(","),
      ...rows.map(r => headers.map(h => `"${r[h]}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `SalesReport_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit", second:"2-digit" })
    : "—";

  const quickBtns = [
    { label: "Today",      key: "today" },
    { label: "Yesterday",  key: "yesterday" },
    { label: "Last 3 days",key: "last3Days" },
    { label: "Last week",  key: "lastWeek" },
    { label: "This month", key: "thisMonth" },
    { label: "2 months",   key: "last2Months" },
    { label: "3 months",   key: "last3Months" },
  ];

  const [quickOpen, setQuickOpen] = useState(false);
  const quickRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (quickRef.current && !quickRef.current.contains(e.target)) setQuickOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Top items chart data (top 10 by quantity)
  const topItemsForChart = itemRankings.slice(0, 10).map(it => it.foodName);
  const topItemsQty = itemRankings.slice(0, 10).map(it => it.quantity || 0);
  const topItemsRevenue = itemRankings.slice(0, 10).map(it => it.totalAmount || 0);

  return (
    <div>
      {/* ── Filter bar ── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end", marginBottom: 14 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 180px" }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Search items</span>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.faint }}>🔍</span>
              <input
                type="text"
                placeholder="Search food items…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                disabled={loading}
                style={{ ...inputSt, width: "100%", paddingLeft: 30 }}
              />
            </div>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>From date</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={loading}
              style={{ ...inputSt, colorScheme: "light" }} />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>To date</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={loading}
              style={{ ...inputSt, colorScheme: "light" }} />
          </label>

          <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
            <Btn primary onClick={getSalesReport} disabled={loading}>↺ Fetch</Btn>
            <Btn onClick={handleExport} disabled={loading || filteredData.length === 0}>⬇ Export CSV</Btn>
            <Btn onClick={clearFilters} disabled={loading}>✕ Clear</Btn>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Session</span>
            <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} disabled={loading}
              style={{ ...inputSt, cursor: "pointer" }}>
              <option value="All">All Sessions</option>
              <option value="Breakfast">Breakfast</option>
              <option value="Lunch">Lunch</option>
              <option value="Dinner">Dinner</option>
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Hub</span>
            <select
              value={selectHub?.hubId || ""}
              onChange={e => {
                const h = hubs.find(x => x.hubId === e.target.value) || {};
                setSelectHub(h);
              }}
              disabled={loading}
              style={{ ...inputSt, cursor: "pointer" }}
            >
              <option value="">All Hubs</option>
              {hubs.map(h => (
                <option key={h._id} value={h.hubId}>{h.hubId} — {h.hubName || h.name}</option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Quick filters</span>
            <div ref={quickRef} style={{ position: "relative" }}>
              <button
                onClick={() => setQuickOpen(o => !o)}
                disabled={loading}
                style={{
                  ...inputSt, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  background: quickOpen ? T.bg : T.surface,
                }}
              >
                ⚡ Quick Filters <span style={{ marginLeft: "auto", fontSize: 10 }}>▾</span>
              </button>
              {quickOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50,
                  background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
                  boxShadow: T.shadow, minWidth: 160, overflow: "hidden",
                }}>
                  {quickBtns.map(b => (
                    <div key={b.key} onClick={() => { setDateRange(b.key); setQuickOpen(false); }}
                      style={{
                        padding: "9px 14px", fontSize: 13, cursor: "pointer", color: T.text,
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >{b.label}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Sort by date</span>
            <button
              onClick={() => setSortOrder(s => s === "desc" ? "asc" : "desc")}
              disabled={loading}
              style={{ ...inputSt, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, minWidth: 110 }}
            >
              {sortOrder === "desc" ? "↓ Newest" : "↑ Oldest"}
            </button>
          </div>
        </div>

        {(selectHub?.hubId || selectedSession !== "All") && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.muted, alignSelf: "center" }}>Active:</span>
            {selectHub?.hubId      && <Pill label={`Hub: ${selectHub.hubName || selectHub.hubId}`} onRemove={() => setSelectHub({})}           color={T.accent} />}
            {selectedSession !== "All" && <Pill label={`Session: ${selectedSession}`}              onRemove={() => setSelectedSession("All")}  color={T.blue} />}
          </div>
        )}
      </Card>

      {error && (
        <div style={{ background: T.red + "10", border: `1px solid ${T.red}33`, borderRadius: 10, padding: "12px 16px", color: T.red, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Enhanced KPI Row ── */}
      {!loading && filteredData.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 16 }}>
            <KPI icon="🥇" label="Top Item (Qty)" value={itemRankings[0]?.foodName || "—"} sub={`Sold ${itemRankings[0]?.quantity || 0} units`} color={T.accent} />
            <KPI icon="💰" label="Top Item (Rev)" value={topItemRevenue.name || "—"} sub={fmtR(topItemRevenue.revenue)} color={T.green} />
            <KPI icon="📊" label="Avg Price/Unit" value={fmtR(avgPricePerItem)} color={T.blue} />
            <KPI icon="🏷️" label="Unique Items Sold" value={fmt(totalSummary.uniqueItems)} color={T.purple} />
            <KPI icon="📦" label="Total Sold Qty" value={fmt(totalSummary.qty)} color={T.blue} />
            <KPI icon="💰" label="Total Revenue" value={fmtR(totalSummary.revenue)} color={T.accent} />
          </div>

          {/* Charts row: Top items by quantity & revenue */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Card data-chart>
              <Sec children="🥇 Top 10 Items by Quantity Sold" />
              <BarChart labels={topItemsForChart} values={topItemsQty} color={T.accent} height={140} />
            </Card>
            <Card data-chart>
              <Sec children="💰 Top 10 Items by Revenue" />
              <BarChart labels={topItemsForChart} values={topItemsRevenue} color={T.green} height={140} prefix="₹" />
            </Card>
          </div>

          {/* Category breakdown & Daily trend */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Card>
              <Sec children="Category / Group Breakdown" />
              {Object.entries(categoryStats).slice(0, 6).map(([cat, stats], idx) => (
                <div key={cat} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: PALETTE[idx % PALETTE.length] }}>{cat}</span>
                    <span>{fmt(stats.qty)} units · {fmtR(stats.rev)}</span>
                  </div>
                  <Bar pct={parseFloat(pct(stats.qty, totalSummary.qty))} color={PALETTE[idx % PALETTE.length]} />
                </div>
              ))}
            </Card>
            <Card>
              <Sec children="Daily Sales Trend (last 7 days)" />
              {salesTrend.length > 0 ? (
                <LineChart 
                  labels={salesTrend.slice(-7).map(d => d.date.slice(5))}
                  datasets={[
                    { label: "Revenue (₹)", values: salesTrend.slice(-7).map(d => d.rev), color: T.green },
                    { label: "Quantity", values: salesTrend.slice(-7).map(d => d.qty), color: T.blue }
                  ]}
                  height={140}
                />
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: T.faint }}>No trend data available</div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* ── Ranked Items Table (highest sold quantity first) ── */}
      <Card>
        <Sec
          children={`📊 Sales Report — Ranked by Quantity Sold (${filteredData.length} items)`}
          right={
            loading
              ? <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.muted, fontSize: 12 }}>
                  <div style={{ width: 12, height: 12, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Loading…
                </span>
              : `${startDate} → ${endDate}`
          }
        />

        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: T.muted }}>
            <div style={{ width: 28, height: 28, border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 12px" }} />
            Loading sales data…
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Item Name</th>
                  <th>Price (₹)</th>
                  <th>Sold Qty</th>
                  <th>Total Amount (₹)</th>
                  <th>Avg Price/Unit</th>
                  <th>Last Sold Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 40, color: T.faint }}>
                      No sales data for selected filters
                    </td>
                  </tr>
                ) : (
                  itemRankings.map((item, idx) => {
                    const avgPerUnit = item.quantity ? (item.totalAmount / item.quantity).toFixed(2) : 0;
                    return (
                      <tr key={idx}>
                        <td style={{ fontWeight: 800, color: idx < 3 ? T.accent : T.faint, fontSize: idx < 3 ? 16 : 13 }}>
                          #{idx+1} {idx === 0 && "🥇"} {idx === 1 && "🥈"} {idx === 2 && "🥉"}
                        </td>
                        <td style={{ fontWeight: 700 }}>{item.foodName || "—"}</td>
                        <td style={{ color: T.muted }}>₹{Number(item.price || 0).toFixed(2)}</td>
                        <td>
                          <span style={{
                            display: "inline-block",
                            background: T.accent + "12", color: T.accent,
                            border: `1px solid ${T.accent}33`,
                            borderRadius: 20, padding: "3px 12px",
                            fontSize: 13, fontWeight: 800,
                          }}>{item.quantity || 0}</span>
                        </td>
                        <td style={{ fontWeight: 800, color: T.green }}>₹{Number(item.totalAmount || 0).toLocaleString("en-IN")}</td>
                        <td style={{ color: T.blue }}>₹{avgPerUnit}</td>
                        <td style={{ color: T.muted, fontSize: 12 }}>{fmtDate(item.lastSoldTime)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {filteredData.length > 0 && (
                <tfoot>
                  <tr style={{ background: T.bg }}>
                    <td colSpan={3} style={{ fontWeight: 800, fontSize: 13 }}>Total / Avg</td>
                    <td style={{ fontWeight: 800, color: T.accent }}>{fmt(totalSummary.qty)}</td>
                    <td style={{ fontWeight: 800, color: T.green }}>{fmtR(totalSummary.revenue)}</td>
                    <td style={{ fontWeight: 800, color: T.blue }}>{fmtR(avgPricePerItem)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD (unchanged)
═══════════════════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const [loading, setLoading]       = useState(false);
  const [period, setPeriod]         = useState("week");
  const [hubFilter, setHubFilter]   = useState("all");
  const [session, setSession]       = useState("all");
  const [customS, setCustomS]       = useState("");
  const [customE, setCustomE]       = useState("");
  const [hubs, setHubs]             = useState([]);
  const [metrics, setMetrics]       = useState(null);
  const [orders, setOrders]         = useState([]);
  const [error, setError]           = useState(null);
  const [tab, setTab]               = useState("overview");
  const runId = useRef(0);

  /* ── Fetch hubs ── */
  useEffect(() => {
    axios.get(`${API}/Hub/hubs`, { headers: hdr() })
      .then(r => setHubs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setHubs([]));
  }, []);

  /* ── Date range ── */
  const getRange = useCallback((p) => {
    if (p === "custom") {
      return {
        start: customS ? new Date(customS) : new Date(0),
        end:   customE ? new Date(customE + "T23:59:59") : new Date(),
      };
    }
    const now = new Date(), end = new Date(); end.setHours(23, 59, 59, 999);
    let start = new Date();
    if (p === "today")        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    else if (p === "week")    { start = new Date(now); start.setDate(now.getDate() - 7); start.setHours(0,0,0,0); }
    else if (p === "month")   start = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (p === "quarter") start = new Date(now.getFullYear(), Math.floor(now.getMonth()/3)*3, 1);
    else if (p === "year")    start = new Date(now.getFullYear(), 0, 1);
    else                      start = new Date(0);
    return { start, end };
  }, [customS, customE]);

  /* ── Fetch orders ── */
  const fetchOrders = useCallback(async (start, end, hubId, sess) => {
    const p = { page: 1, limit: 10000 };
    if (start && end) {
      p.dateFilterType = "custom";
      p.startDate = start.toISOString().split("T")[0];
      p.endDate   = end.toISOString().split("T")[0];
    }
    if (hubId && hubId !== "all") p.hubId = hubId;
    if (sess  && sess  !== "all") p.session = sess;
    try {
      const r = await axios.get(`${API}/admin/getallordersfilter`, { params: p, headers: hdr() });
      return r.data?.data?.orders || [];
    } catch { return []; }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/User/registeruser`, { params: { page: 1, limit: 10000 }, headers: hdr() });
      return r.data?.success || [];
    } catch { return []; }
  }, []);

  /* ── Compute metrics ── */
  const compute = useCallback(async () => {
    const id = ++runId.current;
    setLoading(true); setError(null);
    try {
      const { start, end } = getRange(period);
      const hubId = hubFilter === "all" ? null : hubFilter;

      const [allUsers, raw] = await Promise.all([
        fetchUsers(),
        fetchOrders(start, end, hubId, session),
      ]);
      if (id !== runId.current) return;

      const totalRev   = raw.reduce((s, o) => s + (o.allTotal || 0), 0);
      const subTotals  = raw.reduce((s, o) => s + (o.subTotal || 0), 0);
      const taxTotal   = raw.reduce((s, o) => s + (o.tax || 0), 0);
      const delivChg   = raw.reduce((s, o) => s + (o.deliveryCharge || 0), 0);
      const offerSav   = raw.reduce((s, o) => s + (o.offerSavings || 0), 0);
      const walletUsed = raw.reduce((s, o) => s + (o.discountWallet || 0), 0);
      const couponSav  = raw.reduce((s, o) => s + (o.coupon || 0), 0);
      const n          = raw.length;
      const aov        = n ? totalRev / n : 0;

      const custIds = new Set(raw.map(o => (o.customerId?._id || o.customerId)?.toString()));
      const activeU = custIds.size;
      const ordersByCust = {};
      raw.forEach(o => {
        const id = (o.customerId?._id || o.customerId)?.toString();
        if (id) ordersByCust[id] = (ordersByCust[id] || 0) + 1;
      });
      const repeatCusts = Object.values(ordersByCust).filter(c => c >= 2).length;
      const repeatRate  = activeU ? (repeatCusts / activeU) * 100 : 0;
      const highVal     = Object.values(ordersByCust).filter(c => c > 5).length;
      const oneTime     = Object.values(ordersByCust).filter(c => c === 1).length;
      const newUsers    = allUsers.filter(u => {
        const d = new Date(u.createdAt || u.registeredAt);
        return d >= start && d <= end;
      }).length;

      const countBy  = (arr, key) => arr.reduce((m, o) => { const v = (typeof key === "function" ? key(o) : o[key]) || "Unknown"; m[v] = (m[v] || 0) + 1; return m; }, {});
      const revenueBy = (arr, key) => arr.reduce((m, o) => { const v = (typeof key === "function" ? key(o) : o[key]) || "Unknown"; m[v] = (m[v] || 0) + (o.allTotal || 0); return m; }, {});

      const statusMap    = countBy(raw, o => o.status || o.orderstatus);
      const sessionMap   = countBy(raw, o => o.session || o.slot);
      const payMap       = countBy(raw, "paymentmethod");
      const dtypeMap     = countBy(raw, "deliveryType");
      const custTypeMap  = countBy(raw, "customerType");
      const orderTypeMap = countBy(raw, "ordertype");

      const hubRevMap  = revenueBy(raw, "hubName");
      const hubCntMap  = countBy(raw, "hubName");
      const hubCustMap = raw.reduce((m, o) => {
        const h = o.hubName || "Unknown";
        if (!m[h]) m[h] = new Set();
        m[h].add((o.customerId?._id || o.customerId)?.toString());
        return m;
      }, {});
      const hubStats = Object.keys(hubCntMap).map(h => ({
        hub: h, orders: hubCntMap[h], revenue: hubRevMap[h] || 0,
        customers: hubCustMap[h]?.size || 0,
        aov: hubCntMap[h] ? (hubRevMap[h] || 0) / hubCntMap[h] : 0,
        pct: pct(hubCntMap[h], n),
      })).sort((a, b) => b.orders - a.orders);

      const ratedOrders   = raw.filter(o => o.ratings?.order?.rating != null);
      const avgRating     = ratedOrders.length ? (ratedOrders.reduce((s, o) => s + o.ratings.order.rating, 0) / ratedOrders.length).toFixed(1) : "—";
      const ratedDelivery = raw.filter(o => o.ratings?.delivery?.rating != null);
      const avgDelRating  = ratedDelivery.length ? (ratedDelivery.reduce((s, o) => s + o.ratings.delivery.rating, 0) / ratedDelivery.length).toFixed(1) : "—";

      const dayLabels = [], dayOrders = [], dayRevenue = [], dayP = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0,0,0,0);
        const nx = new Date(d); nx.setDate(d.getDate() + 1);
        dayLabels.push(d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }));
        dayP.push(fetchOrders(d, nx, hubId, session));
      }
      const dayR = await Promise.all(dayP);
      if (id !== runId.current) return;
      dayR.forEach(dr => {
        dayOrders.push(dr.length);
        dayRevenue.push(dr.reduce((s, o) => s + (o.allTotal || 0), 0));
      });

      const moLabels = [], moRevenue = [], moOrders = [], moP = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(); d.setMonth(d.getMonth() - i);
        moLabels.push(d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }));
        moP.push(fetchOrders(
          new Date(d.getFullYear(), d.getMonth(), 1),
          new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59),
          hubId, session
        ));
      }
      const moR = await Promise.all(moP);
      if (id !== runId.current) return;
      moR.forEach(mr => {
        moRevenue.push(mr.reduce((s, o) => s + (o.allTotal || 0), 0));
        moOrders.push(mr.length);
      });

      const cancelled  = statusMap["Cancelled"] || 0;
      const delivered  = statusMap["Delivered"]  || 0;
      const cancelRate = pct(cancelled, n);
      const delivRate  = pct(delivered, n);

      setOrders(raw);
      setMetrics({
        n, totalRev, subTotals, taxTotal, delivChg, offerSav, walletUsed, couponSav,
        aov, repeatRate: repeatRate.toFixed(1), highVal, oneTime, repeatCusts,
        activeU, newUsers, totalUsers: allUsers.length,
        activePct: pct(activeU, allUsers.length),
        cancelRate, delivRate, cancelled, delivered,
        avgRating, avgDelRating, ratedCount: ratedOrders.length,
        statusMap, sessionMap, payMap, dtypeMap, custTypeMap, orderTypeMap,
        hubStats, topHub: hubStats[0]?.hub || "—",
        revPerCust: activeU ? (totalRev / activeU).toFixed(0) : 0,
        dayLabels, dayOrders, dayRevenue,
        moLabels, moRevenue, moOrders,
      });
    } catch (e) {
      console.error(e);
      setError("Failed to load data. Check your connection and try again.");
    } finally {
      if (id === runId.current) setLoading(false);
    }
  }, [period, hubFilter, session, fetchOrders, fetchUsers, getRange]);

  useEffect(() => { compute(); }, [compute]);

  const selHubName = hubFilter === "all" ? null : (hubs.find(h => h._id === hubFilter)?.hubName || hubFilter);
  const tabs = ["overview", "orders", "revenue", "hubs", "customers", "hub customers", "sales report"];

  /* ── RENDER ── */
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Nunito', 'Segoe UI', sans-serif", color: T.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        select, input, button { font-family: inherit; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
        @keyframes sk { 0%,100%{opacity:1}50%{opacity:0.4} }
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: white; color: ${T.text}; }
        table { border-collapse: collapse; width: 100%; }
        th { text-align: left; font-size: 11px; color: ${T.muted}; font-weight: 700;
             text-transform: uppercase; letter-spacing: 0.6px;
             padding: 8px 12px; border-bottom: 2px solid ${T.border}; }
        td { padding: 9px 12px; font-size: 13px; color: ${T.text};
             border-bottom: 1px solid ${T.borderL}; vertical-align: middle; }
        tr:last-child td { border-bottom: none; }
        tr:hover td { background: ${T.bg}; }
      `}</style>

      {/* ── NAVBAR ── */}
      <div style={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        padding: "12px 24px", display: "flex", justifyContent: "space-between",
        alignItems: "center", position: "sticky", top: 0, zIndex: 100,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 34, height: 34, background: T.accent, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          }}>🍱</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 17, letterSpacing: -0.4 }}>DailyDish</div>
            <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Analytics</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: T.muted }}>
              <div style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Loading…
            </div>
          )}
          <div style={{ fontSize: 12, color: T.muted }}>
            {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 20px 40px" }}>

        {/* ── FILTERS (hidden on hub customers tab) ── */}
        {tab !== "hub customers" && tab !== "sales report" && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
              <Sel label="Period" value={period} onChange={v => { setPeriod(v); if (v !== "custom") { setCustomS(""); setCustomE(""); } }}>
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </Sel>
              {period === "custom" && (
                <>
                  <DateIn label="From" value={customS} onChange={setCustomS} />
                  <DateIn label="To"   value={customE} onChange={setCustomE} />
                </>
              )}
              <Sel label="Hub" value={hubFilter} onChange={setHubFilter}>
                <option value="all">All Hubs</option>
                {hubs.map(h => (
                  <option key={h._id} value={h._id}>{h.hubName || h.name}</option>
                ))}
              </Sel>
              <Sel label="Session" value={session} onChange={setSession}>
                <option value="all">All Sessions</option>
                <option value="Breakfast">Breakfast</option>
                <option value="Lunch">Lunch</option>
                <option value="Dinner">Dinner</option>
              </Sel>
              <Btn primary onClick={compute} disabled={loading}>↺ Refresh</Btn>
              {(hubFilter !== "all" || session !== "all") && (
                <Btn onClick={() => { setHubFilter("all"); setSession("all"); }}>✕ Clear</Btn>
              )}
            </div>
            {(selHubName || session !== "all") && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: T.muted, alignSelf: "center" }}>Filtered by:</span>
                {selHubName        && <Pill label={`Hub: ${selHubName}`}  onRemove={() => setHubFilter("all")} color={T.accent} />}
                {session !== "all" && <Pill label={`Session: ${session}`} onRemove={() => setSession("all")}  color={T.blue} />}
              </div>
            )}
          </Card>
        )}

        {error && (
          <div style={{ background: T.red + "10", border: `1px solid ${T.red}33`, borderRadius: 10, padding: "12px 16px", color: T.red, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
            ⚠ {error}
          </div>
        )}

        {/* ── TABS ── */}
        <div style={{ display: "flex", gap: 2, marginBottom: 18, borderBottom: `2px solid ${T.border}`, overflowX: "auto" }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: "none", border: "none",
              borderBottom: tab === t ? `2px solid ${T.accent}` : "2px solid transparent",
              color: tab === t ? T.accent : T.muted,
              padding: "9px 18px", fontSize: 13, fontWeight: 700,
              cursor: "pointer", marginBottom: -2, textTransform: "capitalize",
              letterSpacing: 0.3, transition: "color 0.15s", whiteSpace: "nowrap",
            }}>{t}</button>
          ))}
        </div>

        {/* ════════════════════════════════════════════
            TAB: OVERVIEW
        ════════════════════════════════════════════ */}
        {tab === "overview" && metrics && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 14, marginBottom: 16 }}>
              <KPI icon="📦" label="Total Orders"    value={fmt(metrics.n)}           sub="in period"                color={T.blue}   loading={loading} />
              <KPI icon="💰" label="Gross Revenue"   value={fmtR(metrics.totalRev)}   sub="incl. tax & delivery"    color={T.green}  loading={loading} />
              <KPI icon="🧾" label="Avg Order Value" value={fmtR(metrics.aov)}        sub="per transaction"         color={T.accent} loading={loading} />
              <KPI icon="🔄" label="Repeat Rate"     value={metrics.repeatRate + "%"} sub="≥2 orders"               color={T.purple} loading={loading} />
              <KPI icon="❌" label="Cancel Rate"     value={metrics.cancelRate + "%"} sub={`${metrics.cancelled} orders`} color={T.red} loading={loading} />
              <KPI icon="✅" label="Delivery Rate"   value={metrics.delivRate + "%"}  sub={`${metrics.delivered} delivered`} color={T.green} loading={loading} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <Card data-chart>
                <Sec children="Daily Orders — Last 7 Days" />
                <BarChart labels={metrics.dayLabels} values={metrics.dayOrders} color={T.blue} height={140} />
              </Card>
              <Card data-chart>
                <Sec children="Daily Revenue — Last 7 Days" />
                <BarChart labels={metrics.dayLabels} values={metrics.dayRevenue} color={T.green} height={140} prefix="₹" />
              </Card>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
              <Card>
                <Sec children="Sessions" />
                <div style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                  <Donut segments={Object.entries(metrics.sessionMap).map(([k, v], i) => ({ label: k, value: v, color: PALETTE[i % PALETTE.length] }))} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {Object.entries(metrics.sessionMap).map(([s, v], i) => (
                      <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                        <span style={{ color: T.muted, flex: 1 }}>{s}</span>
                        <strong>{v}</strong>
                        <span style={{ color: T.faint, fontSize: 11 }}>{pct(v, metrics.n)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <Card>
                <Sec children="Order Status" />
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {Object.entries(metrics.statusMap).sort((a,b) => b[1]-a[1]).map(([s, v], i) => {
                    const col = s === "Delivered" ? T.green : s === "Cancelled" ? T.red : s === "Confirmed" ? T.blue : s === "Pending" ? T.yellow : PALETTE[i % PALETTE.length];
                    return (
                      <div key={s}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: T.text, fontWeight: 600 }}>{s}</span>
                          <span style={{ color: T.muted }}>{v} <span style={{ fontSize: 11 }}>({pct(v, metrics.n)}%)</span></span>
                        </div>
                        <Bar pct={parseFloat(pct(v, metrics.n))} color={col} />
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card>
                <Sec children="Payment Methods" />
                <div style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                  <Donut segments={Object.entries(metrics.payMap).map(([k, v], i) => ({ label: k, value: v, color: PALETTE[i % PALETTE.length] }))} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {Object.entries(metrics.payMap).sort((a,b)=>b[1]-a[1]).map(([s, v], i) => (
                      <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                        <span style={{ color: T.muted, flex: 1 }}>{s || "Unknown"}</span>
                        <strong>{v}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
              {[
                { icon: "👥", label: "Total Users",       value: fmt(metrics.totalUsers) },
                { icon: "🆕", label: "New Users",         value: fmt(metrics.newUsers),      sub: "registered in period" },
                { icon: "🎯", label: "Active Customers",  value: fmt(metrics.activeU),       sub: metrics.activePct + "% of all" },
                { icon: "🥇", label: "High-Value (>5)",   value: fmt(metrics.highVal) },
                { icon: "⭐", label: "Avg Food Rating",   value: metrics.avgRating + " / 5", sub: `${metrics.ratedCount} rated` },
                { icon: "🚴", label: "Avg Delivery Rating", value: metrics.avgDelRating + " / 5" },
                { icon: "💸", label: "Rev / Customer",    value: fmtR(metrics.revPerCust) },
                { icon: "🏆", label: "Top Hub",           value: metrics.topHub },
                { icon: "🎁", label: "Wallet Used",       value: fmtR(metrics.walletUsed) },
                { icon: "🏷️", label: "Coupon Savings",    value: fmtR(metrics.couponSav) },
                { icon: "🚚", label: "Delivery Charges",  value: fmtR(metrics.delivChg) },
                { icon: "🧮", label: "Tax Collected",     value: fmtR(metrics.taxTotal) },
              ].map(s => (
                <div key={s.label} style={{
                  background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
                  padding: "14px 16px", boxShadow: T.shadow,
                }}>
                  <div style={{ fontSize: 18, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginTop: 3 }}>{s.value}</div>
                  {s.sub && <div style={{ fontSize: 11, color: T.faint, marginTop: 2 }}>{s.sub}</div>}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════
            TAB: ORDERS
        ════════════════════════════════════════════ */}
        {tab === "orders" && metrics && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 16 }}>
              <KPI icon="📦" label="Total Orders" value={fmt(metrics.n)}           color={T.blue}  loading={loading} />
              <KPI icon="✅" label="Delivered"    value={fmt(metrics.delivered)}   color={T.green} loading={loading} />
              <KPI icon="❌" label="Cancelled"    value={fmt(metrics.cancelled)}   color={T.red}   loading={loading} />
              <KPI icon="❌" label="Cancel Rate"  value={metrics.cancelRate + "%"} color={T.red}   loading={loading} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
              <Card>
                <Sec children="Order Type" />
                {Object.entries(metrics.orderTypeMap).map(([k, v], i) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <Tag color={PALETTE[i % PALETTE.length]}>{k}</Tag>
                    <span style={{ fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </Card>
              <Card>
                <Sec children="Customer Type" />
                {Object.entries(metrics.custTypeMap).map(([k, v], i) => (
                  <div key={k} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{k}</span>
                      <span style={{ color: T.muted }}>{v} ({pct(v, metrics.n)}%)</span>
                    </div>
                    <Bar pct={parseFloat(pct(v, metrics.n))} color={PALETTE[i % PALETTE.length]} />
                  </div>
                ))}
              </Card>
              <Card>
                <Sec children="Delivery Type" />
                {Object.entries(metrics.dtypeMap).map(([k, v], i) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.borderL}` }}>
                    <span style={{ fontSize: 13, textTransform: "capitalize", fontWeight: 600 }}>{k}</span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{v}</span>
                      <Tag color={PALETTE[i]}>{pct(v, metrics.n)}%</Tag>
                    </div>
                  </div>
                ))}
              </Card>
            </div>

            <Card>
              <Sec children="Recent Orders" right={`${Math.min(orders.length, 50)} of ${orders.length}`} />
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Hub</th>
                      <th>Session</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 50).map(o => (
                      <tr key={o._id}>
                        <td style={{ fontFamily: "monospace", fontSize: 12, color: T.blue }}>
                          #{(o.orderid || o.orderId || o._id?.slice(-8) || "—")}
                        </td>
                        <td style={{ fontWeight: 600 }}>{o.username || "—"}</td>
                        <td style={{ color: T.muted, fontSize: 12 }}>{o.Mobilenumber || "—"}</td>
                        <td><Tag color={T.accent}>{o.hubName || "—"}</Tag></td>
                        <td style={{ color: T.muted, textTransform: "capitalize" }}>{o.session || o.slot || "—"}</td>
                        <td style={{ fontWeight: 700, color: T.green }}>{fmtR(o.allTotal)}</td>
                        <td style={{ fontSize: 12 }}>{o.paymentmethod || "—"}</td>
                        <td style={{ fontSize: 12 }}>{o.ordertype || "—"}</td>
                        <td><StatusTag s={o.status || o.orderstatus} /></td>
                        <td style={{ color: T.muted, fontSize: 12 }}>
                          {o.createdAt ? new Date(o.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : o.Placedon || "—"}
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: T.faint }}>No orders for selected filters</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* ════════════════════════════════════════════
            TAB: REVENUE
        ════════════════════════════════════════════ */}
        {tab === "revenue" && metrics && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 16 }}>
              <KPI icon="💰" label="Gross Revenue"    value={fmtR(metrics.totalRev)}   sub="allTotal sum"   color={T.green}  loading={loading} />
              <KPI icon="🍱" label="Food Subtotal"    value={fmtR(metrics.subTotals)}  sub="before tax"     color={T.accent} loading={loading} />
              <KPI icon="🧮" label="Tax Collected"    value={fmtR(metrics.taxTotal)}                        color={T.yellow} loading={loading} />
              <KPI icon="🚚" label="Delivery Fees"    value={fmtR(metrics.delivChg)}                        color={T.blue}   loading={loading} />
              <KPI icon="🎁" label="Wallet Discounts" value={fmtR(metrics.walletUsed)}                      color={T.purple} loading={loading} />
              <KPI icon="🏷️" label="Coupon Discounts" value={fmtR(metrics.couponSav)}                       color={T.teal}   loading={loading} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card data-chart>
                <Sec children="Monthly Revenue (6 Months)" />
                <BarChart labels={metrics.moLabels} values={metrics.moRevenue} color={T.green} height={150} prefix="₹" />
              </Card>
              <Card data-chart>
                <Sec children="Monthly Orders (6 Months)" />
                <BarChart labels={metrics.moLabels} values={metrics.moOrders}  color={T.blue}  height={150} />
              </Card>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════
            TAB: HUBS
        ════════════════════════════════════════════ */}
        {tab === "hubs" && metrics && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 16 }}>
              {metrics.hubStats.map((h, i) => (
                <Card key={h.hub} style={{ borderLeft: `4px solid ${PALETTE[i % PALETTE.length]}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{h.hub}</div>
                    <Tag color={PALETTE[i % PALETTE.length]}>{h.pct}%</Tag>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Orders",    value: fmt(h.orders),          color: PALETTE[i % PALETTE.length] },
                      { label: "Revenue",   value: fmtR(h.revenue),        color: T.green },
                      { label: "Customers", value: fmt(h.customers),       color: T.blue },
                      { label: "AOV",       value: fmtR(h.aov.toFixed(0)), color: T.yellow },
                    ].map(s => (
                      <div key={s.label} style={{ background: T.bg, borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: s.color, marginTop: 3 }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
              {metrics.hubStats.length === 0 && (
                <div style={{ gridColumn: "1/-1", textAlign: "center", color: T.faint, padding: 40 }}>No hub data</div>
              )}
            </div>

            <Card>
              <Sec children="Hub Comparison Table" />
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Hub</th><th>Orders</th><th>Revenue</th><th>Customers</th><th>AOV</th><th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.hubStats.map((h, i) => (
                      <tr key={h.hub}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                            <strong>{h.hub}</strong>
                          </div>
                        </td>
                        <td style={{ fontWeight: 700 }}>{fmt(h.orders)}</td>
                        <td style={{ color: T.green, fontWeight: 700 }}>{fmtR(h.revenue)}</td>
                        <td>{fmt(h.customers)}</td>
                        <td style={{ color: T.yellow, fontWeight: 700 }}>{fmtR(h.aov.toFixed(0))}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
                            <div style={{ flex: 1, height: 6, background: T.borderL, borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ width: h.pct + "%", height: "100%", background: PALETTE[i % PALETTE.length], borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: 12, color: T.muted, minWidth: 36 }}>{h.pct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* ════════════════════════════════════════════
            TAB: CUSTOMERS
        ════════════════════════════════════════════ */}
        {tab === "customers" && metrics && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 14, marginBottom: 16 }}>
              <KPI icon="👥" label="Total Registered" value={fmt(metrics.totalUsers)}  color={T.blue}   loading={loading} />
              <KPI icon="🆕" label="New in Period"    value={fmt(metrics.newUsers)}    color={T.green}  loading={loading} />
              <KPI icon="🎯" label="Active (Ordered)" value={fmt(metrics.activeU)}     sub={metrics.activePct + "% of all"} color={T.accent} loading={loading} />
              <KPI icon="🔄" label="Repeat Rate"      value={metrics.repeatRate + "%"} sub={`${metrics.repeatCusts} customers`} color={T.purple} loading={loading} />
              <KPI icon="🥇" label="High-Value >5"    value={fmt(metrics.highVal)}     color={T.yellow} loading={loading} />
              <KPI icon="1️⃣" label="One-Time Buyers"  value={fmt(metrics.oneTime)}     color={T.muted}  loading={loading} />
              <KPI icon="💸" label="Rev / Customer"   value={fmtR(metrics.revPerCust)} color={T.green}  loading={loading} />
              <KPI icon="⭐" label="Food Rating"      value={`${metrics.avgRating}/5`} sub={`${metrics.ratedCount} rated`} color={T.yellow} loading={loading} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card>
                <Sec children="Customer Segments" />
                {[
                  { label: "High-Value (>5 orders)", value: metrics.highVal,    color: T.yellow, max: metrics.activeU },
                  { label: "Repeat (2–5 orders)",    value: metrics.repeatCusts - metrics.highVal, color: T.green, max: metrics.activeU },
                  { label: "One-Time Buyers",         value: metrics.oneTime,    color: T.blue,   max: metrics.activeU },
                ].map(s => (
                  <div key={s.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                      <span style={{ fontWeight: 600 }}>{s.label}</span>
                      <span><strong>{s.value}</strong> <span style={{ color: T.faint, fontSize: 11 }}>({pct(s.value, s.max)}%)</span></span>
                    </div>
                    <Bar pct={parseFloat(pct(s.value, s.max))} color={s.color} />
                  </div>
                ))}
              </Card>
              <Card>
                <Sec children="Customer Type Mix" />
                <div style={{ display: "flex", alignItems: "center", gap: 20, justifyContent: "center" }}>
                  <Donut segments={Object.entries(metrics.custTypeMap).map(([k, v], i) => ({ label: k, value: v, color: PALETTE[i % PALETTE.length] }))} size={120} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {Object.entries(metrics.custTypeMap).map(([k, v], i) => (
                      <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                        <span style={{ color: T.muted, flex: 1 }}>{k}</span>
                        <strong>{v}</strong>
                        <span style={{ color: T.faint, fontSize: 11 }}>{pct(v, metrics.n)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════
            TAB: HUB CUSTOMERS
        ════════════════════════════════════════════ */}
        {tab === "hub customers" && (
          <HubCustomersTab hubs={hubs} />
        )}

        {/* ════════════════════════════════════════════
            TAB: SALES REPORT (Enhanced)
        ════════════════════════════════════════════ */}
        {tab === "sales report" && (
          <SalesReportTab hubs={hubs} />
        )}

        {!metrics && !loading && !error && tab !== "hub customers" && tab !== "sales report" && (
          <div style={{ textAlign: "center", color: T.faint, padding: 60 }}>
            Click Refresh to load dashboard data.
          </div>
        )}
      </div>
    </div>
  );
}